import { Hub } from 'aws-amplify/utils';
import { logger } from 'src/logger';
import { metricsPublisher } from 'src/metrics';
import { METRIC_NAME } from 'src/types';

import { isTokenExpirationError } from './controlPlane/errors';

const DEFAULT_BASE_DELAY_MS = 500;
const DEFAULT_MAX_RETRIES = 3;

/**
 * Type-safe higher-order function that wraps an API method with token refresh handling.
 * This version completely avoids using 'any' types while maintaining type safety.
 *
 * It implements exponential backoff for retries after token refresh.
 *
 * @example
 * // Usage with automatic type inference
 * const wrappedMethod = withTokenRefresh(myApiMethod);
 *
 * // Usage with custom options
 * const wrappedMethod = withTokenRefresh(myApiMethod, {
 *   baseDelayMs: 800,
 *   maxRetries: 4
 * });
 *
 * @param apiMethod - The API method function to wrap with token refresh handling
 * @param options - Configuration options
 * @param options.baseDelayMs - Base delay in milliseconds for exponential backoff (default: 500ms)
 * @param options.maxRetries - Maximum number of retry attempts (default: 3)
 * @returns A new function with the same signature but with token refresh handling
 */
export function withTokenRefresh<Args extends readonly unknown[], Return>(
  apiMethod: (...args: Args) => Promise<Return>,
  options?: { baseDelayMs?: number; maxRetries?: number },
): (...args: Args) => Promise<Return> {
  // Create and return a new function with the same signature
  const wrappedMethod = async (...args: Args): Promise<Return> => {
    const baseDelayMs = options?.baseDelayMs ?? DEFAULT_BASE_DELAY_MS;
    const maxRetries = options?.maxRetries ?? DEFAULT_MAX_RETRIES;
    let retryCount = 0;

    while (true) {
      try {
        // Attempt API call with current credentials
        return await apiMethod(...args);
      } catch (error: unknown) {
        // If it's not a token expiration error or we've exhausted retries, re-throw
        const isTokenError = isTokenExpirationError(error);
        if (!isTokenError || retryCount >= maxRetries) {
          if (isTokenError) {
            metricsPublisher.emitCount(METRIC_NAME.TOKEN_REFRESH_FAILURE);
          }
          throw error;
        }

        logger.info(
          `Token expired during API call to ${apiMethod.name}, triggering refresh (attempt ${retryCount + 1}/${maxRetries})`,
        );

        // Dispatch token refresh event
        Hub.dispatch('auth', { event: 'tokenRefresh', data: {} });

        // Calculate delay with exponential backoff
        const delayMs = Math.pow(2, retryCount) * baseDelayMs; // e.g., 500ms, 1s, 2s
        logger.debug(`Waiting ${delayMs}ms before retry`);

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, delayMs));

        // Increment retry counter
        retryCount++;

        logger.debug(
          `Retrying ${apiMethod.name} with refreshed credentials (attempt ${retryCount}/${maxRetries})`,
        );
      }
    }
  };

  // Use Object.defineProperty to preserve the original function name
  Object.defineProperty(wrappedMethod, 'name', {
    value: `${apiMethod.name}WithTokenRefresh`,
    configurable: true,
  });

  // Type assertion is safe here because we're preserving the exact function signature
  return wrappedMethod as (...args: Args) => Promise<Return>;
}
